import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import WatchCard from "@/components/WatchCard";
import { SlidersHorizontal, X } from "lucide-react";

const CatalogPage = () => {
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [groups, setGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [watches, setWatches] = useState([]);
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const selected = useMemo(() => {
    const c = params.get("category");
    return c ? c.split(",").filter(Boolean) : [];
  }, [params]);

  const selectedMv = useMemo(() => {
    const m = params.get("movement");
    return m ? m.split(",").filter(Boolean) : [];
  }, [params]);

  useEffect(() => {
    http.get("/categories").then((r) => setCategories(r.data));
    http.get("/movements").then((r) => setMovements(r.data));
    http.get("/movement-groups").then((r) => {
      setGroups(r.data);
      const o = {}; r.data.forEach((g) => { o[g.slug] = true; }); setOpenGroups(o);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (selected.length) q.category = selected.join(",");
    if (selectedMv.length) q.movement = selectedMv.join(",");
    http.get("/watches", { params: q }).then((r) => {
      setWatches(r.data);
      setLoading(false);
    });
  }, [selected.join(","), selectedMv.join(",")]);

  const toggle = (slug) => {
    const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug];
    const p = {};
    if (next.length) p.category = next.join(",");
    if (selectedMv.length) p.movement = selectedMv.join(",");
    setParams(p);
  };

  const toggleMv = (slug) => {
    const next = selectedMv.includes(slug) ? selectedMv.filter((s) => s !== slug) : [...selectedMv, slug];
    const p = {};
    if (selected.length) p.category = selected.join(",");
    if (next.length) p.movement = next.join(",");
    setParams(p);
  };

  const clearAll = () => setParams({});

  const SidebarContent = (
    <div className="space-y-10">
      <div>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl">Modelos</h3>
          {(selected.length > 0 || selectedMv.length > 0) && (
            <button
              onClick={clearAll}
              data-testid="filter-clear"
              className="text-[10px] uppercase tracking-[0.24em] text-[var(--aq-muted)] hover:text-black"
            >
              Limpiar
            </button>
          )}
        </div>
        <ul className="space-y-3">
          {categories.map((c) => (
            <li key={c.id}>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid={`filter-${c.slug}`}
                  checked={selected.includes(c.slug)}
                  onChange={() => toggle(c.slug)}
                  className="aq-checkbox"
                />
                <span className="text-sm text-[var(--aq-ink)] group-hover:opacity-70 transition">
                  {c.name}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="font-display text-xl mb-5">Movimiento</h3>
        <div className="space-y-4">
          {groups.map((g) => {
            const mvs = movements.filter((m) => (m.group_slug || "otros") === g.slug);
            const isOpen = openGroups[g.slug];
            return (
              <div key={g.id} data-testid={`mvgroup-${g.slug}`}>
                <button
                  type="button"
                  onClick={() => setOpenGroups({ ...openGroups, [g.slug]: !isOpen })}
                  data-testid={`mvgroup-toggle-${g.slug}`}
                  className="w-full flex items-center justify-between text-xs uppercase tracking-[0.24em] py-2 border-b border-[var(--aq-border)] hover:border-black"
                >
                  <span>{g.name} <span className="text-[var(--aq-muted)]">({mvs.length})</span></span>
                  <span>{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && (
                  <ul className="space-y-3 mt-3 pl-1">
                    {mvs.length === 0 ? (
                      <li className="text-xs text-[var(--aq-muted)] italic">Sin movimientos aún</li>
                    ) : mvs.map((m) => (
                      <li key={m.id}>
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input type="checkbox" data-testid={`movfilter-${m.slug}`}
                            checked={selectedMv.includes(m.slug)} onChange={() => toggleMv(m.slug)}
                            className="aq-checkbox" />
                          <span className="text-sm text-[var(--aq-ink)] group-hover:opacity-70">{m.name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="pt-6 border-t border-[var(--aq-border)]">
        <p className="overline mb-3">Consulta</p>
        <p className="text-xs text-[var(--aq-muted)] leading-relaxed">
          Cada pieza de AQ Watches es hecha a la medida. Selecciona un modelo y escríbenos por WhatsApp para iniciar tu diseño.
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="catalog-page" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-12 lg:pt-20 pb-24">
      <div className="text-center mb-14">
        <p className="overline mb-4">Catálogo</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">Colección AQ Watches</h1>
        <p className="text-[var(--aq-muted)] max-w-xl mx-auto text-sm sm:text-base">
          Explora nuestras piezas personalizadas y diseños a la medida. Filtra por familia de modelo en el panel lateral.
        </p>
      </div>

      <div className="flex items-center justify-between lg:hidden mb-6">
        <p className="text-xs text-[var(--aq-muted)] tracking-wider">
          {watches.length} relojes
        </p>
        <button
          data-testid="mobile-filters-toggle"
          onClick={() => setFiltersOpen(true)}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] border border-black px-4 py-2"
        >
          <SlidersHorizontal size={14} /> Filtros{(selected.length + selectedMv.length) > 0 ? ` (${selected.length + selectedMv.length})` : ""}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 lg:gap-16">
        <aside className="hidden lg:block sticky top-28 self-start" data-testid="catalog-sidebar">
          {SidebarContent}
        </aside>

        {filtersOpen && (
          <div className="fixed inset-0 z-50 bg-white lg:hidden overflow-y-auto" data-testid="mobile-filters">
            <div className="px-6 py-6 flex items-center justify-between border-b border-[var(--aq-border)]">
              <h3 className="font-display text-xl">Filtros</h3>
              <button onClick={() => setFiltersOpen(false)} aria-label="cerrar" data-testid="mobile-filters-close">
                <X size={22} />
              </button>
            </div>
            <div className="px-6 py-8">{SidebarContent}</div>
            <div className="px-6 pb-10">
              <button onClick={() => setFiltersOpen(false)} className="btn-primary w-full">
                Ver {watches.length} relojes
              </button>
            </div>
          </div>
        )}

        <section>
          <div className="hidden lg:flex items-center justify-between mb-6">
            <p className="text-xs text-[var(--aq-muted)] tracking-wider">
              {watches.length} relojes
            </p>
            <p className="text-xs text-[var(--aq-muted)] tracking-wider">
              Orden: <span className="text-black">Más recientes</span>
            </p>
          </div>

          {loading ? (
            <p className="text-[var(--aq-muted)] text-sm">Cargando…</p>
          ) : watches.length === 0 ? (
            <div className="border border-[var(--aq-border)] p-12 text-center">
              <p className="font-display text-2xl mb-2">No hay relojes con estos filtros.</p>
              <p className="text-sm text-[var(--aq-muted)]">Intenta limpiar los filtros o seleccionar otros modelos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {watches.map((w) => <WatchCard key={w.id} watch={w} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CatalogPage;
